import React, { useMemo, useRef, useState } from 'react';
import { buildForecastSeries } from '../../util/forecastChart';

/**
 * ForecastChartLarge — the full-size rate history + forecast chart shown when the
 * sidebar mini chart is expanded. Dependency-free React SVG (ratesearch has no
 * charting lib): dated x-axis, price y-axis with gridlines, solid history line,
 * dashed forecast line, shaded confidence band, and a hover tooltip.
 *
 * Customer-facing: it is only ever passed the SELLING series (historySelling /
 * forecastSelling) — never raw cost.
 */

const C_HIST = '#16788B';
const C_FC = '#B87514';
const C_BAND = 'rgba(184,117,20,0.14)';
const C_GRID = '#EAEFF5';
const C_AXIS = '#8494A6';

const money = (n) => `$${Math.round(n).toLocaleString('en-US')}`;
const monthLabel = (t) =>
  new Date(t).toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });

// ~`count` "nice" tick values spanning [min,max].
function ticks(min, max, count) {
  if (!(max > min)) return [min];
  const raw = (max - min) / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || mag;
  const out = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-6; v += step) out.push(v);
  return out;
}

export default function ForecastChartLarge({ history = [], forecast = null, laneName, confidence, mobile = false }) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);

  // Mobile: taller, portrait-ish viewBox with roomier axes + fewer x labels.
  const W = mobile ? 360 : 720;
  const H = mobile ? 430 : 360;
  const PAD = mobile ? { l: 52, r: 14, t: 16, b: 46 } : { l: 60, r: 18, t: 18, b: 38 };
  const xTickEvery = mobile ? 4 : 7;

  const { hist, fc } = useMemo(
    () => buildForecastSeries({ history, forecast, todayISO: new Date().toISOString().slice(0, 10) }),
    [history, forecast],
  );

  const model = useMemo(() => {
    const all = [...hist, ...fc];
    if (all.length < 2) return null;
    const ts = all.map((p) => p.t);
    const tMin = Math.min(...ts);
    const tMax = Math.max(...ts);
    const vals = [];
    hist.forEach((p) => vals.push(p.price));
    fc.forEach((p) => {
      vals.push(p.price);
      if (Number.isFinite(p.lo)) vals.push(p.lo);
      if (Number.isFinite(p.hi)) vals.push(p.hi);
    });
    let vMin = Math.min(...vals);
    let vMax = Math.max(...vals);
    const gutter = (vMax - vMin || vMax || 1) * 0.08;
    vMin -= gutter;
    vMax += gutter;

    const x = (t) => PAD.l + (tMax === tMin ? 0 : (t - tMin) / (tMax - tMin)) * (W - PAD.l - PAD.r);
    const y = (v) => PAD.t + (1 - (v - vMin) / (vMax - vMin || 1)) * (H - PAD.t - PAD.b);

    const histPts = hist.map((p) => ({ ...p, cx: x(p.t), cy: y(p.price), kind: 'history' }));
    const fcPts = fc.map((p) => ({ ...p, cx: x(p.t), cy: y(p.price), kind: 'forecast' }));
    // forecast line connects from the last history point
    const fcLine = hist.length ? [histPts[histPts.length - 1], ...fcPts] : fcPts;
    const band =
      fc.length && fc.every((p) => Number.isFinite(p.lo) && Number.isFinite(p.hi))
        ? fc.map((p) => `${x(p.t)},${y(p.hi)}`).concat(fc.map((p) => `${x(p.t)},${y(p.lo)}`).reverse()).join(' ')
        : '';

    return {
      x, y, tMin, tMax, vMin, vMax, histPts, fcPts, fcLine, band,
      yTicks: ticks(vMin, vMax, mobile ? 4 : 5),
      xTicks: [...histPts, ...fcPts].filter((_, i, a) => i % Math.max(1, Math.ceil(a.length / xTickEvery)) === 0 || i === a.length - 1),
      dividerX: hist.length ? x(hist[hist.length - 1].t) : PAD.l,
      all: [...histPts, ...fcPts],
    };
  }, [hist, fc, mobile]);

  if (!model) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#8494A6' }}>Not enough rate history to chart this lane yet.</div>;
  }

  const onMove = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W; // map to viewBox space
    let best = null;
    for (const p of model.all) {
      const d = Math.abs(p.cx - px);
      if (!best || d < best.d) best = { p, d };
    }
    setHover(best ? best.p : null);
  };

  const line = (pts) => pts.map((p) => `${p.cx},${p.cy}`).join(' ');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: '#28374A' }}>{laneName || 'Rate history & forecast'}</div>
        {confidence && (
          <div style={{ font: '600 11px ui-monospace, monospace', letterSpacing: '0.06em', color: '#56697E' }}>
            confidence: {confidence}
          </div>
        )}
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label="rate history and forecast"
      >
        {/* y gridlines + labels */}
        {model.yTicks.map((v) => (
          <g key={`y${v}`}>
            <line x1={PAD.l} y1={model.y(v)} x2={W - PAD.r} y2={model.y(v)} stroke={C_GRID} strokeWidth="1" />
            <text x={PAD.l - 8} y={model.y(v) + 3} textAnchor="end" fontSize="11" fill={C_AXIS} fontFamily="ui-monospace, monospace">
              {money(v)}
            </text>
          </g>
        ))}
        {/* x labels */}
        {model.xTicks.map((p) => (
          <text key={`x${p.t}`} x={p.cx} y={H - PAD.b + 18} textAnchor="middle" fontSize="11" fill={C_AXIS} fontFamily="ui-monospace, monospace">
            {monthLabel(p.t)}
          </text>
        ))}
        {/* confidence band */}
        {model.band && <polygon points={model.band} fill={C_BAND} />}
        {/* history / forecast divider */}
        <line x1={model.dividerX} y1={PAD.t} x2={model.dividerX} y2={H - PAD.b} stroke="#C9D4E0" strokeWidth="1" strokeDasharray="3 3" />
        {/* history + forecast lines */}
        {model.histPts.length > 1 && <polyline points={line(model.histPts)} fill="none" stroke={C_HIST} strokeWidth="2.2" />}
        {model.fcLine.length > 1 && <polyline points={line(model.fcLine)} fill="none" stroke={C_FC} strokeWidth="2.2" strokeDasharray="6 4" />}
        {/* markers */}
        {model.histPts.map((p) => <circle key={`h${p.t}`} cx={p.cx} cy={p.cy} r="2.6" fill={C_HIST} />)}
        {model.fcPts.map((p) => <circle key={`f${p.t}`} cx={p.cx} cy={p.cy} r="2.6" fill={C_FC} />)}
        {/* hover guide + tooltip */}
        {hover && (
          <g>
            <line x1={hover.cx} y1={PAD.t} x2={hover.cx} y2={H - PAD.b} stroke="#B5C2D0" strokeWidth="1" />
            <circle cx={hover.cx} cy={hover.cy} r="4.5" fill="#fff" stroke={hover.kind === 'history' ? C_HIST : C_FC} strokeWidth="2" />
            {(() => {
              const boxW = 130;
              const bx = Math.min(Math.max(hover.cx - boxW / 2, PAD.l), W - PAD.r - boxW);
              const by = Math.max(hover.cy - 52, PAD.t);
              return (
                <g>
                  <rect x={bx} y={by} width={boxW} height={40} rx="5" fill="#28374A" opacity="0.96" />
                  <text x={bx + boxW / 2} y={by + 16} textAnchor="middle" fontSize="11" fill="#B9C6D6" fontFamily="ui-monospace, monospace">
                    {monthLabel(hover.t)} · {hover.kind}
                  </text>
                  <text x={bx + boxW / 2} y={by + 31} textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff" fontFamily="ui-monospace, monospace">
                    {money(hover.price)}
                  </text>
                </g>
              );
            })()}
          </g>
        )}
      </svg>
      {/* legend */}
      <div style={{ display: 'flex', gap: 18, font: '11px ui-monospace, monospace', color: '#56697E', marginTop: 8, justifyContent: 'center' }}>
        <span><span style={{ display: 'inline-block', width: 16, borderTop: `2px solid ${C_HIST}`, verticalAlign: 'middle', marginRight: 5 }} />history</span>
        <span><span style={{ display: 'inline-block', width: 16, borderTop: `2px dashed ${C_FC}`, verticalAlign: 'middle', marginRight: 5 }} />forecast</span>
        <span><span style={{ display: 'inline-block', width: 14, height: 9, background: C_BAND, verticalAlign: 'middle', marginRight: 5 }} />likely range</span>
      </div>
    </div>
  );
}
